import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	WarningMessageBox
} from "../../components/styled/common";
import { useEventTagContext } from "../../context/EventTagContext/EventTagContext";
import { EventTagListItem } from "../../context/EventTagContext/constants";
import { getEventTagIconUrl } from "../../components/IconPicker/constants";
import TagComponent from "../../components/TagComponent";

const EventTagList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<EventTagListItem | null>(null);
	const { eventTagList, clearEventTagList, clearEventTagFormData, getEventTagList, removeEventTag, operationStatus } = useEventTagContext();

	const [searchText, setSearchText] = useState("");

	const navigate = useNavigate();

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return search
			? eventTagList.filter((x) => (x.tagName ?? "").toLocaleLowerCase("tr-TR").includes(search))
			: eventTagList;
	}, [eventTagList, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearEventTagList();
		clearEventTagFormData();
		getEventTagList();

		return () => {
			clearEventTagList();
		};
	}, []);

	const columns = useMemo(() => [
		createColumn({
			title: "Etiket Adı",
			dataIndex: "tagName",
			width: 120,
			sorterType: "string",
		}),
		createColumn({
			title: "İkon",
			dataIndex: "tagIcon",
			width: 60,
			render: (text, record) =>
				record.tagIcon ? <img src={getEventTagIconUrl(record.tagIcon)} alt={record.tagIcon} style={{ width: 25, height: 25, objectFit: "contain" }} /> : "-",
		}),
		createColumn({
			title: "Görünüm",
			dataIndex: "preview",
			width: 100,
			render: (text, record) => <TagComponent label={record.tagName} icon={record.tagIcon} />,
		}),
		createColumn({
			title: "Eklenme Tarihi",
			dataIndex: "creationDate",
			width: 50,
			sorterType: "string",
			render: (text, record) =>
				record.creationDate ? tableDateFormatter(record.creationDate, "DD.MM.YYYY") : "-",
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 20,
			render: (text, record: any) => {
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`/event-tags/edit/${record.eventTagId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(record);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>Proje Etiketleri</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Etiket Adı Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate("/event-tags/add")}
							>
                                Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as any[]}
					/>
				)}
			</Container>
			<Modal
				title="Etiketi Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
                        İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeEventTag(currentItem.eventTagId);
							}
							setIsModalVisible(false);
						}}
					>
                        Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.tagName ?? ""}"`}</strong> etiketini silmek istediğinizden emin misiniz?</p>
				{currentItem?.isBeingUsed && (
					<WarningMessageBox>
						Bu etiketi halihazırda kullanan projeler bulunmaktadır. Silinmesi durumunda, projelerde etiketin kullanımı da sonlandırılacaktır.
					</WarningMessageBox>
				)}
			</Modal>
		</div>
	);
};

export default EventTagList;
